import '.a7{position:relative;}.ssr-plugin'
import '.fz{max-width:560px;}.ssr-plugin'
import '.bc{overflow:hidden;}.ssr-plugin'
import '.am{height:100%;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.bv{border-radius:20px;}.ssr-plugin'
import '.g0{animation:300ms tilt-soft cubic-bezier(0.30,-0.00,0.60,1.10);}.ssr-plugin'
import '.ck{flex-grow:1;}.ssr-plugin'
import '.ce{overflow:auto;}.ssr-plugin'
import '.g1{scroll-behavior:smooth;}.ssr-plugin'
import '.bn{height:auto;}.ssr-plugin'
import '.al{flex:1;}.ssr-plugin'
import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import { Style, styled } from 'inlines'
import { useBasedEndpoints } from '../../providers/Endpoints/index.js'
import { useLanguage } from '../LanguageSelect/index.js'
import { useClient, useQuery } from '@based/sdk/react'
import { Fields, FieldsProps } from './Fields.js'
import { Header, HeaderProps } from './Header.js'
import { normalize } from '../../utils/text.js'
import { colors } from '../../tokens/colors.js'
import { UiSchema, UiSchemaProp, UiSchemaType } from '../../types/schema.js'
import { useCmsParams } from '../../hooks/useCmsParams.js'
import { useDependency } from '../../hooks/useDependency.js'
import { AppHeader } from '../AppHeader/index.js'

const EMPTY_PAYLOAD = Object.freeze({})
const EMPTY_SCHEMA = Object.freeze({ types: {} as any })

const FormContext = createContext<{
  copyObj: any
  schemaType: string
  initialData?: any
  submit: () => void | Promise<void>
  isDirty?: boolean
  changedFields?: string[]
} | null>(null)

export type TransformFields = (
  fields: Record<any, UiSchemaProp>,
  values: Record<string, any>,
) => Record<any, UiSchemaProp>

export type ContentEditorProps = {
  schema?: UiSchema
  schemaType?: string
  id?: number
  customEndpoints?: Partial<ReturnType<typeof useBasedEndpoints>>
  onChange?: (values: any, changed: any) => void
  lastUpdatedAt?: (value: number) => void
  changedFields?: (x: boolean) => void
  onSubmit?: (fields: any, dirtyFields: any) => void | Promise<void>
  transformFields?: TransformFields
  exclude?: string[]
  include?: string[]
  disabled?: string[]
  children?: React.ReactNode
  preview?: boolean
  style?: Style
}

const AppPreview = ({ children }) => {
  return (
    <div className="a7 fz bc"
    >
      {children}
    </div>
  )
}

export const ContentEditor = ({
  schema: schemaProp = EMPTY_SCHEMA as any,
  schemaType,
  id,
  customEndpoints,
  onChange,
  lastUpdatedAt,
  changedFields,
  transformFields,
  onSubmit,
  exclude,
  include,
  disabled,
  children,
  preview,
  style,
}: ContentEditorProps) => {
  const schemaTypeSafe = schemaType ?? ''
  if (!schemaTypeSafe) return null

  const instanceId = useRef(Math.random().toString(16).slice(2)).current

  const client = useClient()
  const [params, go] = useCmsParams()
  const scrollContainer = useRef<HTMLDivElement | null>(null)
  const [lang] = useLanguage()
  const endpoints = useBasedEndpoints()

  const customEndpointsDep = useDependency(customEndpoints)
  const endpointsMerged = useMemo(() => {
    if (!customEndpoints) return endpoints
    const newEndpoints = { ...endpoints, ...customEndpoints }
    console.log('[ContentEditor] New endpoints: ', newEndpoints)
    return newEndpoints
  }, [endpoints, customEndpointsDep])

  const { data: schemaData } = useQuery(endpointsMerged.schema)

  const schema = (
    Object.keys(schemaProp?.types || {}).length
      ? schemaProp
      : Object.keys(schemaData?.types || {}).length
        ? schemaData
        : schemaProp
  ) as UiSchema

  const typeSchemaRaw: UiSchemaType = (schema?.[schemaTypeSafe || '_root'] ||
    schema?.types?.[schemaTypeSafe || '']?.props ||
    {}) as any

  const typeSchema: Record<string, any> = useMemo(() => {
    const res: Record<string, any> = {}
    for (const key in typeSchemaRaw) {
      const value = (typeSchemaRaw as any)[key]
      if (Boolean(value) || value?.hide === true) {
        // avoid mutating source schema
        const copied = value && typeof value === 'object' ? { ...value } : value
        if (copied && typeof copied === 'object') delete (copied as any).default
        res[key] = copied
      }
    }
    return res
  }, [typeSchemaRaw])

  const includeDep = useDependency(include)
  const excludeDep = useDependency(exclude)

  const includeFinal: string[] = useMemo(() => {
    const base = (include ?? Object.keys(typeSchema)).slice()
    if (!exclude?.length) return base
    return base.filter((item) => !exclude.includes(item))
  }, [includeDep, excludeDep, typeSchema, exclude])

  const queryPayload = useMemo(() => {
    if (!id) return EMPTY_PAYLOAD
    return { id, type: schemaTypeSafe, include: includeFinal }
  }, [id, schemaTypeSafe, includeFinal])

  const { data: initialData, checksum: initialDataChecksum } = useQuery(
    (queryPayload as any)?.id !== undefined ? endpointsMerged.query : '',
    queryPayload,
  )

  useEffect(() => {
    if (!id) return

    console.log('[ContentEditor] Query:', {
      instanceId,
      endpoint: endpointsMerged.query,
      ...(queryPayload as any),
    })
  }, [id, endpointsMerged.query, queryPayload, instanceId])

  const [values, setValues] = useState<any>(undefined)

  // always-latest values snapshot for submit (prevents stale state on fast submit)
  const valuesRef = useRef<any>(undefined)

  // baseline snapshot for "dirty" (lets you clear changes immediately after saving)
  const savedRef = useRef<any>(undefined)
  const [savedVersion, setSavedVersion] = useState(0)

  useEffect(() => {
    if (!initialData) return

    setValues((prev) => {
      if (!prev) {
        valuesRef.current = initialData
        savedRef.current = initialData
        setSavedVersion((v) => v + 1)
        return initialData
      }

      const localDirty =
        Object.keys(areThereChangedFields(prev, initialData)).length > 0

      if (!localDirty) {
        valuesRef.current = initialData
        savedRef.current = initialData
        setSavedVersion((v) => v + 1)
        return initialData
      }

      return prev
    })

    console.log('[ContentEditor] Data:', initialData)
  }, [initialDataChecksum])

  const changes = useCallback(
    (v: any, name: any) => {
      setValues((prev: any) => {
        const cur = prev ?? {}

        const prevField = cur[name]
        const nextField = typeof v === 'function' ? v(prevField) : v

        if (Object.is(prevField, nextField)) return prev

        const next = { ...cur, [name]: nextField }

        // keep synchronous latest for submit
        valuesRef.current = next

        onChange?.(next, { [name]: nextField })
        return next
      })
    },
    [onChange],
  )

  useEffect(() => {
    if (lastUpdatedAt && initialData?.updatedAt) {
      lastUpdatedAt(initialData.updatedAt)
    }
  }, [lastUpdatedAt, initialData?.updatedAt])

  const dirty = useMemo(() => {
    return areThereChangedFields(values, savedRef.current)
  }, [values, savedVersion])

  const dirtyKeys = useMemo(() => Object.keys(dirty), [dirty])
  const isDirty = dirtyKeys.length > 0

  useEffect(() => {
    if (!changedFields) return
    changedFields(isDirty)
  }, [changedFields, isDirty])

  const transformed = useMemo(() => {
    if (!transformFields) return undefined
    return transformFields(typeSchemaRaw as Record<any, UiSchemaProp>, values)
  }, [transformFields, typeSchemaRaw, values])

  const effectiveSchemaType = transformed || typeSchema
  const effectiveSchemaTypeDep = useDependency(effectiveSchemaType)

  useEffect(() => {
    if (!effectiveSchemaType) return

    setValues((prev: any) => {
      const cur = prev ?? {}
      const patch = computeDefaultsPatch(effectiveSchemaType, cur)
      if (!patch) return prev

      const next = applyDefaultsPatch(cur, patch)

      // keep synchronous latest for submit
      valuesRef.current = next

      return next
    })
  }, [effectiveSchemaTypeDep])

  const childrenArray = React.Children.toArray(children)

  const appHeader = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === AppHeader,
  )

  const contentPreview = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === AppPreview,
  )

  const submit = useCallback(async () => {
    const currentValues = valuesRef.current ?? values
    const baseline = savedRef.current ?? initialData

    const changed = areThereChangedFields(currentValues, baseline)

    if (Object.keys(changed).length > 0 && onSubmit) {
      await onSubmit(currentValues, changed)

      // clear local dirty after successful custom submit
      savedRef.current = currentValues
      setSavedVersion((v) => v + 1)
      return
    }

    if (!onSubmit) {
      if (!params.id) {
        try {
          const newId = await client.call(endpointsMerged.create, {
            type: schemaTypeSafe,
          })

          await client
            .call(endpointsMerged.update, {
              ...changed,
              id: newId,
              type: schemaTypeSafe,
              $$lang: lang,
            })
            .catch(console.error)

          // clear local dirty after successful save
          savedRef.current = currentValues
          setSavedVersion((v) => v + 1)

          go({ section: normalize(params.section), page: 'edit', id: newId })
        } catch (err: any) {
          console.error('‼️ ', err.message || 'Unknown error')
        }
      } else {
        await client
          .call(endpointsMerged.update, {
            ...changed,
            id: id ?? params.id,
            type: schemaTypeSafe,
            $$lang: lang,
          })
          .catch(console.error)

        // clear local dirty after successful save
        savedRef.current = currentValues
        setSavedVersion((v) => v + 1)
      }
    }
  }, [
    values,
    initialData,
    onSubmit,
    params.id,
    client,
    endpointsMerged,
    schemaTypeSafe,
    id,
    lang,
    go,
    params.section,
  ])

  if (!Object.keys(schema || typeSchema || {}).length) return null

  const ctxValue = useMemo(
    () => ({
      copyObj: values,
      schemaType: schemaTypeSafe,
      initialData,
      submit,
      isDirty,
      changedFields: dirtyKeys,
    }),
    [values, schemaTypeSafe, initialDataChecksum, submit, isDirty, dirtyKeys],
  )

  return (
    <FormContext.Provider value={ctxValue}>
      {appHeader}
      <div className="am a bv bc"
        style={{
          boxShadow: `0 8px 12px 0 ${colors.black10}`,
          border: `1px solid ${colors.neutral10}`,
        }}
      >
        <styled.div className="g0 a am ck"
          style={{
            background: `linear-gradient(${colors.white5}),
            linear-gradient(${colors.neutralInverted100})`,
            ...style,
          }}
        >
          {!children && (
            <Fields
              schemaType={effectiveSchemaType}
              copyObj={values}
              changes={changes}
              exclude={exclude}
              disabled={disabled}
              checksum={initialDataChecksum}
            />
          )}

          <div className="ce ck am g1"
            ref={scrollContainer}
          >
            {React.Children.map(children, (child) => {
              if (React.isValidElement(child) && child.type === Header) {
                return React.cloneElement(
                  child as React.ReactElement<HeaderProps>,
                  { initialData, schemaType: schemaTypeSafe },
                )
              }
              if (React.isValidElement(child) && child.type === Fields) {
                return React.cloneElement(
                  child as React.ReactElement<FieldsProps>,
                  {
                    schemaType: effectiveSchemaType,
                    copyObj: values,
                    changes,
                    exclude,
                    disabled,
                    checksum: initialDataChecksum,
                  },
                )
              }
              if (React.isValidElement(child) && child.type === AppHeader)
                return null
              if (React.isValidElement(child) && child.type === AppPreview)
                return null
              return child
            })}
          </div>

          {preview && (
            <div className="bn al"
              style={{
                borderLeft: `1px solid ${colors.neutral10}`,
                maxWidth:
                  initialData?.gameType === 'sudoku' ? 'max-content' : 560,
              }}
            >
              {contentPreview}
            </div>
          )}
        </styled.div>
      </div>
    </FormContext.Provider>
  )
}

ContentEditor.Fields = Fields
ContentEditor.Header = Header
ContentEditor.AppPreview = AppPreview

ContentEditor.Submit = ({
  children,
}: {
  children: (props: {
    submit?: () => void | Promise<void>
    isDirty?: boolean
    changedFields?: string[]
  }) => React.ReactNode
}) => {
  const context = useContext(FormContext)
  if (!context) return null

  return (
    <>
      {children({
        submit: context.submit || (() => {}),
        isDirty: context.isDirty || false,
        changedFields: context.changedFields || [],
      })}
    </>
  )
}

export const useContentEditor = () => {
  const context = useContext(FormContext)
  if (!context)
    throw new Error('useContentEditor must be used within ContentEditor')

  return {
    submit: context.submit,
    isDirty: Boolean(context.isDirty),
    copyObj: context.copyObj,
    initialData: context.initialData,
    schemaType: context.schemaType,
  }
}

const computeDefaultsPatch = (schemaType: Record<string, any>, values: any) => {
  let patch: any = null

  for (const field in schemaType) {
    const prop = schemaType[field]
    if (!prop) continue

    const hasField = values[field] !== undefined

    if (!hasField && prop?.default !== undefined) {
      patch ??= {}
      patch[field] = prop.default
      continue
    }

    if (prop?.type === 'object' && prop?.props) {
      const obj = values[field]
      const objIsMissing = obj === undefined || obj === null

      for (const k in prop.props) {
        const child = prop.props[k]
        if (child?.default === undefined) continue

        const childMissing =
          objIsMissing || (obj && obj[k] === undefined) || (!obj && true)

        if (childMissing) {
          patch ??= {}
          patch[field] ??= objIsMissing ? {} : { ...(obj || {}) }
          patch[field][k] = child.default
        }
      }
    }
  }

  return patch
}

const applyDefaultsPatch = (values: any, patch: any) => {
  const next = { ...values }
  for (const key in patch) {
    const val = patch[key]
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      next[key] = { ...(values?.[key] || {}), ...val }
    } else {
      next[key] = val
    }
  }
  return next
}

const areThereChangedFields = (a, b) => {
  if (!a && !b) return {}

  const current = a ?? {}
  const initial = b ?? {}
  const changed = {}

  for (const key in current) {
    const val = current[key]
    const old = initial[key]

    if (key === 'meta' && typeof val === 'object' && val !== null) {
      const meta = {}
      let hasMeta = false

      for (const k in val) {
        if (JSON.stringify(val[k]) !== JSON.stringify(old?.[k])) {
          meta[k] = val[k]
          hasMeta = true
        }
      }

      for (const k in old || {}) {
        if (!(k in val)) {
          meta[k] = undefined
          hasMeta = true
        }
      }

      if (hasMeta) changed[key] = meta
    } else if (JSON.stringify(val) !== JSON.stringify(old)) {
      changed[key] = val
    }
  }

  for (const key in initial) {
    if (!(key in current)) changed[key] = undefined
  }

  return changed
}
